import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import {
  CreateOrUpdateRequisitionPayload,
  RequisitionListParams,
  RequisitionResponse,
  RequisitionsResponse,
  ReviewRequisitionPayload
} from '../types/requisitions.type'

type UseRequisitionsOptions = RequisitionListParams & {
  id?: string
}

export const useRequisitions = (arg: UseRequisitionsOptions = {}) => {
  const { id, limit = 20, page = 1, query, status, statuses, unit_id } = arg

  const { data: requisitionsResponse, isLoading: requisitionsIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RequisitionsResponse>('requisitions', {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(query && { query }),
          ...(status && { status }),
          ...(statuses?.length && { status: statuses.join(',') }),
          ...(unit_id && { unit_id })
        }
      })
      return data
    },
    queryKey: ['requisitions', { limit, page, query, status, statuses, unit_id }]
  })

  const { data: singleRequisitionResponse, isLoading: requisitionIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<RequisitionResponse>(`requisitions/${id}`)
      return data
    },
    queryKey: ['requisition', id]
  })

  const { mutateAsync: createRequisition } = useMutation({
    mutationFn: async (payload: CreateOrUpdateRequisitionPayload) => {
      const { data } = await AXIOS_INSTANCE.post('requisitions', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] })
    }
  })

  const { mutateAsync: updateRequisition } = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CreateOrUpdateRequisitionPayload }) => {
      const { data } = await AXIOS_INSTANCE.patch(`requisitions/${id}`, payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] })
      queryClient.invalidateQueries({ queryKey: ['requisition', id] })
    }
  })

  const { mutateAsync: reviewRequisition } = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ReviewRequisitionPayload }) => {
      const { data } = await AXIOS_INSTANCE.patch(`requisitions/${id}/review`, payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] })
      queryClient.invalidateQueries({ queryKey: ['requisition', id] })
    }
  })

  const { mutateAsync: fulfillRequisition } = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await AXIOS_INSTANCE.post(`requisitions/${id}/fulfill`)
      return data
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['requisitions'] })
      queryClient.invalidateQueries({ queryKey: ['requisition', id] })
    }
  })

  return {
    createRequisition,
    fulfillRequisition,
    requisition: singleRequisitionResponse?.data,
    requisitionIncluded: singleRequisitionResponse?.included,
    requisitionIsLoading,
    requisitions: requisitionsResponse?.data,
    requisitionsIncluded: requisitionsResponse?.included,
    requisitionsIsLoading,
    requisitionsMeta: requisitionsResponse?.meta,
    requisitionsPagination: requisitionsResponse?.meta?.pagination,
    reviewRequisition,
    updateRequisition
  }
}
